import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Lightning, Globe, Wallet, ArrowRight, Check, Bell, Lock } from "@phosphor-icons/react";
import api from "../lib/api";
import { flag, fmtEUR } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PaymentLogos from "../components/PaymentLogos";
import LegalNotice from "../components/LegalNotice";
import CryptoFunding from "../components/CryptoFunding";

const PRESET_AMOUNTS = [10, 25, 50, 100];

export default function PayFly() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [rates, setRates] = useState([]);
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [saveCard, setSaveCard] = useState(false);
  const [method, setMethod] = useState("card");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/payg/rates").then(({ data }) => setRates(data)); }, []);

  const final = useMemo(() => {
    const v = custom ? parseFloat(custom) : amount;
    return Number.isFinite(v) ? Math.max(5, Math.min(500, v)) : 0;
  }, [amount, custom]);

  const start = async () => {
    if (!user) { nav("/login"); return; }
    if (!accepted) {
      setError(t("checkout.termsRequired", "Please accept the Terms & Conditions and Privacy Policy to continue."));
      return;
    }
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/payg/checkout-session", {
        amount_eur: final,
        origin_url: window.location.origin,
        save_card: saveCard,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || t("common.error"));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="payfly-page">
      <Header />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-coral-soft/60 blob animate-blob pointer-events-none" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-cobalt-100 blob animate-blob pointer-events-none" style={{ animationDelay: "-4s" }} />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-12 relative">
          <div className="inline-flex items-center gap-2 pill bg-coral-soft/80 text-coral-deep mb-5">
            <Lightning size={14} weight="fill" /> {t("payfly.badge")}
          </div>
          <h1 className="font-display font-bold text-5xl md:text-7xl tracking-tight">{t("payfly.title")}</h1>
          <p className="mt-5 text-ink-700 max-w-2xl text-lg leading-relaxed">{t("payfly.sub")}</p>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.1fr_1fr] gap-6">
          {/* HOW IT WORKS */}
          <div className="card-soft p-8">
            <div className="overline mb-4">{t("payfly.howEyebrow")}</div>
            <h2 className="font-display font-bold text-3xl tracking-tight mb-6">{t("payfly.howTitle")}</h2>
            <ul className="space-y-4">
              {["payfly.bullet1", "payfly.bullet2", "payfly.bullet3", "payfly.bullet4"].map((k) => (
                <li key={k} className="flex items-start gap-3">
                  <div className="mt-0.5 w-6 h-6 rounded-full bg-coral text-white flex items-center justify-center flex-shrink-0">
                    <Check size={14} weight="bold" />
                  </div>
                  <span className="text-ink-900 leading-relaxed">{t(k)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 grid grid-cols-3 gap-px bg-ink-100 rounded-2xl overflow-hidden border border-ink-200">
              <div className="bg-white p-4 text-center">
                <div className="font-display font-bold text-2xl">12</div>
                <div className="text-xs text-ink-500 uppercase tracking-widest mt-1">{t("payfly.statValidity")}</div>
              </div>
              <div className="bg-white p-4 text-center">
                <div className="font-display font-bold text-2xl">120+</div>
                <div className="text-xs text-ink-500 uppercase tracking-widest mt-1">{t("home.statCountries")}</div>
              </div>
              <div className="bg-white p-4 text-center">
                <div className="font-display font-bold text-2xl">€0.92</div>
                <div className="text-xs text-ink-500 uppercase tracking-widest mt-1">{t("payfly.statFromGB")}</div>
              </div>
            </div>
          </div>

          {/* TOPUP CARD */}
          <div className="card-soft p-8 bg-gradient-to-br from-white to-cobalt-50">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-2xl bg-cobalt-500 text-white flex items-center justify-center">
                <Wallet size={20} weight="bold" />
              </div>
              <div className="font-display font-bold text-xl tracking-tight">{t("payfly.topupTitle")}</div>
            </div>
            <p className="text-sm text-ink-700 mb-6">{t("payfly.topupSub")}</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {PRESET_AMOUNTS.map((a) => (
                <button
                  key={a}
                  onClick={() => { setAmount(a); setCustom(""); }}
                  className={`py-3 rounded-2xl font-semibold border ${!custom && amount === a ? "border-cobalt-500 bg-cobalt-500 text-white" : "border-ink-200 bg-white hover:border-cobalt-300"}`}
                  data-testid={`payg-amount-${a}`}
                >
                  €{a}
                </button>
              ))}
            </div>
            <label className="block">
              <span className="text-xs text-ink-500 uppercase tracking-widest font-semibold">{t("payfly.customAmount")}</span>
              <div className="mt-1 flex items-center input-soft !p-0">
                <span className="pl-4 text-ink-500">€</span>
                <input
                  type="number" min="5" max="500" step="1"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="25"
                  className="flex-1 px-2 py-3 outline-none bg-transparent border-0"
                  data-testid="payg-custom-amount"
                />
              </div>
            </label>

            {/* Funding method */}
            <div className="mt-5 grid grid-cols-2 gap-2" data-testid="payfly-method-tabs">
              {[
                { id: "card", label: t("payfly.methodCard", "Card") },
                { id: "crypto", label: t("payfly.methodCrypto", "Crypto") },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => { setMethod(m.id); setError(""); }}
                  className={`py-2.5 rounded-2xl font-semibold text-sm border ${method === m.id ? "border-cobalt-500 bg-cobalt-500 text-white" : "border-ink-200 bg-white hover:border-cobalt-300"}`}
                  data-testid={`payfly-method-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {method === "card" && <PaymentLogos size={20} className="mt-5" testid="payfly-payment-logos" />}

            {/* Remember card for auto top-ups */}
            {method === "card" && (
            <label className="flex items-start gap-3 mt-5 cursor-pointer select-none" data-testid="payfly-remember-card-label">
              <input
                type="checkbox"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-cobalt-500 flex-shrink-0"
                data-testid="payfly-remember-card"
              />
              <span className="text-xs text-ink-700 leading-relaxed">
                <span className="font-semibold">{t("payfly.rememberCard", "Remember this card for auto top-ups")}</span>{" "}
                <span className="text-ink-500">
                  — {t("payfly.rememberCardHelp", "We'll store a token (not the card number) so we can recharge your wallet automatically when it drops below your threshold.")}
                </span>
              </span>
            </label>
            )}

            {/* Terms acceptance — required */}
            <label className="flex items-start gap-3 mt-5 cursor-pointer select-none" data-testid="payfly-terms-label">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-cobalt-500 flex-shrink-0"
                data-testid="payfly-accept-terms"
              />
              <span className="text-xs text-ink-700 leading-relaxed">
                {t("checkout.acceptPre", "I have read and accept the")}{" "}
                <Link to="/legal/terms" target="_blank" className="font-semibold text-cobalt-600 hover:underline">
                  {t("footer.terms")}
                </Link>{" "}
                {t("common.and", "and")}{" "}
                <Link to="/legal/privacy" target="_blank" className="font-semibold text-cobalt-600 hover:underline">
                  {t("footer.privacy")}
                </Link>{".  "}
                {t("checkout.acceptPost", "I authorise BlueRoam Ltd to process this payment.")}
              </span>
            </label>

            {error && (
              <div className="mt-3 text-sm text-destructive bg-red-50 rounded-xl p-3" data-testid="payfly-error">{error}</div>
            )}

            {method === "card" ? (
            <>
            <button
              onClick={start}
              disabled={loading || final < 5 || !accepted}
              className="btn-primary w-full mt-5 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="payg-checkout"
            >
              {loading ? t("checkout.processing") : <>{t("payfly.fund")} {fmtEUR(final)} <ArrowRight size={16} weight="bold" /></>}
            </button>
            <div className="mt-3 flex items-center gap-2 text-xs text-ink-500">
              <Lock size={12} weight="bold" /> {t("checkout.securedByNp", "Payments securely processed by NeptunePay · 256-bit TLS")}
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-ink-500">
              <Bell size={12} /> {t("payfly.optInNote")}
            </div>
            </>
            ) : (
            <CryptoFunding amountEur={final} accepted={accepted} onError={setError} />
            )}

            <div className="mt-5">
              <LegalNotice compact testid="payfly-legal-notice" />
            </div>
          </div>
        </div>
      </section>

      {/* COUNTRY RATES */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="overline mb-3">{t("payfly.ratesEyebrow")}</div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-8">{t("payfly.ratesTitle")}</h2>
          <div className="card-soft overflow-hidden !rounded-3xl">
            <table className="w-full text-sm" data-testid="payfly-rates-table">
              <thead className="bg-cobalt-50 text-xs uppercase tracking-widest text-cobalt-600">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">{t("common.country")}</th>
                  <th className="text-right px-5 py-3 font-semibold">{t("payfly.perGb")}</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.id} className="border-t border-ink-100 hover:bg-sky-soft/40">
                    <td className="px-5 py-3"><span className="text-xl mr-2">{flag(r.country_iso2)}</span> {r.country}</td>
                    <td className="px-5 py-3 text-right font-display font-bold">{fmtEUR(r.retail_eur_per_gb)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
